// src/auth/AuthProvider.tsx
// Updated: 2026-08-04 20:50:00 CDT
// Purpose: Provide shared Supabase authentication without logging session credentials.

import React, {
  createContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type {
  Session,
  User,
} from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue>({
  session: null,
  user: null,
  isLoading: true,
  signOut: async () => {},
});

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadInitialSession = async () => {
      try {
        const {
          data: { session: initialSession },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error("[AuthProvider] getSession error:", error);
        }

        if (!isMounted) return;

        setSession(initialSession ?? null);
        setUser(initialSession?.user ?? null);
        console.debug("[AuthProvider] Initial session resolved:", {
          hasSession: Boolean(initialSession),
        });
      } catch (error) {
        console.error("[AuthProvider] unexpected getSession error:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadInitialSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      console.debug("[AuthProvider] onAuthStateChange:", {
        event,
        hasSession: Boolean(nextSession),
      });
      setSession(nextSession ?? null);
      setUser(nextSession?.user ?? null);
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      isLoading,
      signOut: async () => {
        try {
          const { error } = await supabase.auth.signOut();

          if (error) {
            throw error;
          }

          console.debug("[AuthProvider] signOut success");
        } catch (error) {
          console.error("[AuthProvider] signOut error:", error);
          throw error;
        }
      },
    }),
    [session, user, isLoading],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
